import { createContext, useContext, useState } from 'react'
import { Formik, useFormikContext, Field, useField, Form } from 'formik'
import { number, object, string } from 'yup'
import { Box } from '@xstyled/styled-components'
import { Modal, useModalState } from '@trybeapp/ui.modal'
import { Row, Col } from '@trybeapp/ui.grid'
import { Button } from '@trybeapp/ui.button'
import { Input } from '@trybeapp/ui.input'
import { ConnectedField } from 'components/ConnectedField'
import { DiscountTypeSelect } from 'components/fields/DiscountTypeSelect'
import {
  useAddOrderDiscount,
  useAddOrderItemDiscount,
  useDeleteOrderItemDiscount,
} from 'api/OrderDiscounts'
import { Card } from '@trybeapp/ui.card'
import { Label } from '@trybeapp/ui.label'
import {
  ApiErrorNotification,
  Notification,
  useNotificationContext,
} from 'ui/components/Notification'
import { FormikCurrencyInput } from 'ui/components/Form/Input'
import { useActiveFeatureFlags, useCurrentSite } from 'contexts/SiteConfig'
import { ConfirmationModal, Modal as UiModal, ModalButtons } from 'ui/Modal'
import { FormikDiscountTypeSelect } from 'ui/components/Form/DiscountTypeSelect'
import { Button as UiButton } from 'ui/components/Button'
import { useApplyOrderItemDiscount } from 'api/OrderDiscountsV2'

export const ModalContext = createContext()

export const ModalProvider = ({ children }) => {
  const modal = useModalState()
  const [order, setOrder] = useState()
  const [item, setItem] = useState()

  return (
    <ModalContext.Provider value={{ modal, order, setOrder, item, setItem }}>
      {children}
    </ModalContext.Provider>
  )
}

export const AddOrderDiscountOverlay = () => {
  const { modal, item } = useContext(ModalContext)
  const featureFlags = useActiveFeatureFlags()

  if (featureFlags.includes('discount_types_and_promo_codes')) {
    return modal.visible && item !== null && <AddOrderItemDiscountWrapper />
  }

  return modal.visible && <AddOrderDiscountWrapper />
}

const AddOrderItemDiscountWrapper = () => {
  const { modal, order, item } = useContext(ModalContext)
  const { mutate: addOrderItemDiscount } = useAddOrderItemDiscount()
  const { mutate: applyOrderItemDiscount } = useApplyOrderItemDiscount()
  const notification = useNotificationContext()
  const featureFlags = useActiveFeatureFlags()

  const initialValues = {
    discount_type_id: '',
    discount_type_code: '',
    applicable_for: 'app_only',
    amount_type: '',
    discount_amount: '',
    is_custom_amount: false,
  }

  const validationSchema = object({
    discount_amount: number().when('amount_type', {
      is: 'monetary',
      then: number()
        .required('You must enter an amount for the discount.')
        .min(1, 'This amount cannot be zero.')
        .max(
          order.outstanding_payment_amount,
          "You can't discount more than the remaining cost of the order."
        ),
      otherwise: number().when('amount_type', {
        is: 'percentage',
        then: number()
          .required('You must enter an amount for the discount.')
          .min(1, 'Amount cannot be zero')
          .max(100, "You can't discount more than the total cost of the order."),
      }),
    }),
  })

  const handleSubmitV1 = (vals) => {
    const newVals = { ...vals }
    if (newVals.amount_type === 'monetary') {
      newVals.amount_type = 'fixed_amount'
    }

    addOrderItemDiscount([order.id, item.id, newVals], {
      onSuccess: () => {
        notification.notify(
          <Notification
            title="Discount applied"
            description="The discount was applied successfully"
            variant="success"
            autoDismiss
          />
        )
        modal.hide()
      },
      onError: (e) => {
        notification.notify(<ApiErrorNotification error={e} />)
      },
    })
  }

  const handleSubmitV2 = (vals) => {
    const newVals = { ...vals }

    applyOrderItemDiscount([order.id, item.id, newVals], {
      onSuccess: () => {
        notification.notify(
          <Notification
            title="Discount applied"
            description="The discount was applied successfully"
            variant="success"
            autoDismiss
          />
        )
        modal.hide()
      },
      onError: (e) => {
        notification.notify(<ApiErrorNotification error={e} />)
      },
    })
  }

  return (
    <UiModal isOpen={modal.visible} title="Apply discount to item" onClose={modal.hide} styleReset>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={
          featureFlags.includes('discount_types_and_promo_codes') ? handleSubmitV2 : handleSubmitV1
        }
      >
        <ApplyDiscountToItem modal={modal} />
      </Formik>
    </UiModal>
  )
}

const ApplyDiscountToItem = ({ modal }) => {
  const { currency } = useCurrentSite()
  const { values, setFieldValue } = useFormikContext()
  const [discountType, setDiscountType] = useState(null)

  const isMonetary = discountType?.amount_type === 'monetary'
  const canEdit = discountType?.is_custom_amount === true

  return (
    <Form>
      <div className="mt-4">
        <ConnectedField
          name="discount_type_code"
          type="select"
          component={FormikDiscountTypeSelect}
          label="Discount type"
          onChange={(event) => {
            const discountType = event.target.discountType
            setDiscountType(discountType)

            if (event.target.name === 'discount_type_code') {
              setFieldValue('discount_type_code', event.target.value)
            }

            if (event.target.name === 'amount_type') {
              setFieldValue('amount_type', event.target.value)
            }

            if (event.target.name === 'discount_amount') {
              setFieldValue('discount_amount', event.target.value)
            }
          }}
        />
      </div>

      {values.discount_type_code !== '' && (
        <div className="mt-4">
          <ConnectedField
            name="discount_amount"
            label="Discount amount"
            component={isMonetary ? FormikCurrencyInput : Input}
            currency={isMonetary && currency}
            disabled={!canEdit}
          />
        </div>
      )}

      <ModalButtons>
        <UiButton label="Apply discount" variant="primary" type="submit" />
        <UiButton label="Cancel" onClick={modal.hide} />
      </ModalButtons>
    </Form>
  )
}

export const RemoveDiscountFromItem = ({ discount, orderId, itemId, onClose }) => {
  const { mutate, isLoading: isDeleting } = useDeleteOrderItemDiscount()
  const notification = useNotificationContext()

  const handleDelete = () => {
    mutate([orderId, itemId, discount.id], {
      onSuccess: () => {
        notification.notify(
          <Notification
            title="Discount removed"
            description="The discount was removed successfully"
            variant="success"
            autoDismiss
          />
        )
        onClose()
      },
      onError: (e) => {
        notification.notify(<ApiErrorNotification error={e} />)
      },
    })
  }

  return (
    <ConfirmationModal
      isOpen={discount !== null}
      onClose={onClose}
      title="Remove discount"
      confirmButtonLabel="Remove"
      onConfirm={handleDelete}
      confirming={isDeleting}
    >
      <div className="space-y-2 text-sm text-gray-500">
        <p>Are you sure you want to remove this discount?</p>
        <p>This will remove the discount currently applied to this item.</p>
      </div>
    </ConfirmationModal>
  )
}

const AddOrderDiscountWrapper = () => {
  const { modal, order, item } = useContext(ModalContext)
  const { mutate: addOrderDiscount } = useAddOrderDiscount()
  const { mutate: addOrderItemDiscount } = useAddOrderItemDiscount()
  const { mutate: applyOrderItemDiscount } = useApplyOrderItemDiscount()
  const notification = useNotificationContext()
  const featureFlags = useActiveFeatureFlags()

  const initialValues = {
    amount_type: '',
    fixed_amount: '',
    percentage: '',
    discount_type_id: '',
    is_custom_amount: '',
  }

  const validationSchemaV1 = object({
    amount_type: string().required(),
    fixed_amount: number()
      .when('amount_type', {
        is: 'fixed_amount',
        then: number()
          .required('You must enter an amount for the discount.')
          .min(1, 'Amount cannot be zero'),
      })
      .max(
        order.outstanding_payment_amount,
        "You can't discount more than the remaining cost of the order"
      ),
    percentage: number()
      .when('amount_type', {
        is: 'percentage',
        then: number().required('You must enter an amount for the discount.'),
      })
      .min(1, 'Amount cannot be zero')
      .max(100, "You can't discount more than the total cost of the order"),
  })

  const validationSchemaV2 = object({
    discount_amount: number().when('amount_type', {
      is: 'monetary',
      then: number()
        .required('You must enter an amount for the discount.')
        .min(1, 'This amount cannot be zero.')
        .max(
          order.outstanding_payment_amount,
          "You can't discount more than the remaining cost of the order."
        ),
      otherwise: number().when('amount_type', {
        is: 'percentage',
        then: number()
          .required('You must enter an amount for the discount.')
          .min(1, 'Amount cannot be zero')
          .max(100, "You can't discount more than the total cost of the order."),
      }),
    }),
  })

  const handleSubmitV1 = (values) => {
    const submitValues = { ...values }

    if (submitValues.amount_type === 'fixed_amount') {
      submitValues.fixed_amount = values.fixed_amount
      delete submitValues.percentage
    } else {
      submitValues.percentage = values.percentage
      delete submitValues.fixed_amount
    }

    if (item) {
      addOrderItemDiscount([order.id, item.id, submitValues], {
        onSuccess: () => {
          notification.notify(
            <Notification
              title="Success"
              description="Discount was added"
              variant="success"
              autoDismiss
            />
          )
          modal.hide()
        },
        onError: (error) => {
          notification.notify(<ApiErrorNotification error={error} />)
        },
      })
    } else {
      addOrderDiscount([order.id, submitValues], {
        onSuccess: () => {
          notification.notify(
            <Notification
              title="Success"
              description="Discount was added"
              variant="success"
              autoDismiss
            />
          )
          modal.hide()
        },
      })
    }
  }

  const handleSubmitV2 = (values) => {
    const submitValues = { ...values }

    if (item) {
      applyOrderItemDiscount([order.id, item.id, submitValues], {
        onSuccess: () => {
          notification.notify(
            <Notification
              title="Success"
              description="Discount was added"
              variant="success"
              autoDismiss
            />
          )
          modal.hide()
        },
        onError: (error) => {
          notification.notify(<ApiErrorNotification error={error} />)
        },
      })
    } else {
      addOrderDiscount([order.id, submitValues], {
        onSuccess: () => {
          notification.notify(
            <Notification
              title="Success"
              description="Discount was added"
              variant="success"
              autoDismiss
            />
          )
          modal.hide()
        },
      })
    }
  }

  return (
    <Modal {...modal} size="sm" ariaLabel="Add discount" hideOnClickOutside={false}>
      <Formik
        initialValues={initialValues}
        onSubmit={
          featureFlags.includes('discount_types_and_promo_codes') ? handleSubmitV2 : handleSubmitV1
        }
        validationSchema={
          featureFlags.includes('discount_types_and_promo_codes')
            ? validationSchemaV2
            : validationSchemaV1
        }
        validateOnMount={true}
        enableReinitialize={true}
      >
        <AddDiscountForm />
      </Formik>
    </Modal>
  )
}

const AddDiscountForm = () => {
  const { values, isValid, setValues } = useFormikContext()
  const { isLoading: isAdding } = useAddOrderDiscount()

  const handleDiscountTypeChange = (discountType) => {
    setValues({
      discount_type_id: discountType.id,
      amount_type: discountType.amount_type,
      fixed_amount:
        discountType.amount_type === 'fixed_amount' ? discountType.fixed_amount ?? '' : '',
      percentage: discountType.amount_type === 'percentage' ? discountType.percentage : '',
      is_custom_amount: discountType.is_custom_amount,
    })
  }

  return (
    <Form>
      <Modal.Header backgroundColor="light.800">Add discount</Modal.Header>
      <Modal.Content backgroundColor="pageBackground">
        <Card overflow="visible">
          <Card.Body>
            <Label>Discount type</Label>
            <Field
              value={values.discount_type_id}
              component={DiscountTypeSelect}
              size="lg"
              label="Discount"
              onChange={handleDiscountTypeChange}
            />
            {values.amount_type === 'fixed_amount' && <FixedAmountField />}
            {values.amount_type === 'percentage' && <PercentageField />}
          </Card.Body>
        </Card>
      </Modal.Content>
      <Modal.Footer>
        <Box width={1} display="flex">
          <Button variant="primary" type="submit" disabled={!isValid} loading={isAdding}>
            Add
          </Button>
        </Box>
      </Modal.Footer>
    </Form>
  )
}

const FixedAmountField = () => {
  const [{ value: isCustomAmount }] = useField('is_custom_amount')
  const {
    order: { currency },
  } = useContext(ModalContext)

  return (
    <Row my={3}>
      <Col>
        <ConnectedField
          name="fixed_amount"
          id="fixed_amount"
          component={FormikCurrencyInput}
          currency={currency}
          size="lg"
          label="Discount amount"
          disabled={!isCustomAmount}
        />
      </Col>
    </Row>
  )
}

const PercentageField = () => {
  const [{ value: isCustomAmount }] = useField('is_custom_amount')

  return (
    <Row my={3}>
      <Col>
        <ConnectedField
          name="percentage"
          id="percentage"
          component={Input}
          type="number"
          placeholder="%"
          size="lg"
          label="Discount percentage"
          disabled={!isCustomAmount}
        />
      </Col>
    </Row>
  )
}
